/* © 2017-2026 Booz Allen Hamilton Inc. All Rights Reserved. */

import React, { useCallback } from 'react';
import PropTypes from 'prop-types';
import get from 'lodash/get';
import { FlexRow, FlexCol, RequiredValidation, NumberField } from 'sarsaparilla';

import { LOTTERY_CHOICES_PROP_PATH } from 'ui-permit/shared-components/constants/propPaths';
import { databindings, readMaxGroupSizeRule } from '../../constants/registration';
import { HuntingLotteryApplicationProps } from '../../props';

const propTypes = {
    displayValidationError: PropTypes.bool.isRequired,
    pActions: PropTypes.shape({
        updatePendingReservation: PropTypes.func.isRequired,
        formValidationChange: PropTypes.func.isRequired,
    }).isRequired,
    validation: PropTypes.shape({}).isRequired,
    reservation: HuntingLotteryApplicationProps,
    mdSize: PropTypes.number,
};

const GroupSizeInput = React.memo(
    ({
        reservation,
        pActions,
        validation,
        displayValidationError,
        mdSize = 5,
        permit,
        permitTypeID,
    }) => {
        const groupMinSizePropPath = `${databindings[LOTTERY_CHOICES_PROP_PATH]}[0].group_min_size`;
        const groupMaxSizePropPath = `${databindings[LOTTERY_CHOICES_PROP_PATH]}[0].group_max_size`;
        const validationPropPath = `${reservation.id}_${groupMaxSizePropPath}`; // only validate one since both would have the same value
        const maxGroupSize = readMaxGroupSizeRule(
            permit.id,
            `${permitTypeID}-lottery`,
            permit.rules
        );

        const onFormValidate = useCallback(
            (isInvalid, error) =>
                pActions.formValidationChange(
                    validationPropPath,
                    isInvalid ? error : null
                ),
            [reservation.id]
        );

        const onChangeGroupSize = (v) => {
            pActions.updatePendingReservation(reservation.id, groupMinSizePropPath, v);
            pActions.updatePendingReservation(reservation.id, groupMaxSizePropPath, v);
        };

        const selectedGroupSize = get(reservation, groupMaxSizePropPath, 0);
        const groupSizeIsInvalid =
            !!get(validation, validationPropPath) && displayValidationError;

        if (selectedGroupSize > maxGroupSize) {
            onChangeGroupSize(maxGroupSize);
        }

        return (
            <FlexRow key="groupSize-input">
                <FlexCol xs={6} md={mdSize}>
                    <NumberField
                        visualLabel="Total Group Size"
                        min={1}
                        max={maxGroupSize}
                        onChange={onChangeGroupSize}
                        value={selectedGroupSize}
                        isRequired
                        isInvalid={groupSizeIsInvalid}
                        visualLabelPosition="top"
                        units="members"
                    />

                    <RequiredValidation
                        value={`${selectedGroupSize !== 0 ? selectedGroupSize : ''}`}
                        displayInSummary
                        onValidated={onFormValidate}
                        controlLabel="Group Size"
                    />
                </FlexCol>
            </FlexRow>
        );
    }
);

export default GroupSizeInput;

GroupSizeInput.propTypes = propTypes;
